import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { EmptyState, Loader, PageTitle } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/pages/console/Validations";

function PartnerForm({ p, cats, onClose, onSaved }) {
  const [f, setF] = useState({ name: p?.name || "", category: p?.category || cats[0], link: p?.link || "", active: p ? p.active : true });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (p) fd.append("partner_id", p.id);
      if (file) fd.append("file", file);
      await api.post("/admin/partners", fd);
      toast.success("Partenaire enregistré");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="partner-form">
        <DialogHeader><DialogTitle>{p ? "Modifier le partenaire" : "Nouveau partenaire"}</DialogTitle></DialogHeader>
        <label><span className="mr-label">Nom</span><input data-testid="partner-name" className="mr-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <label><span className="mr-label">Catégorie</span><select data-testid="partner-category" className="mr-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{cats.map((c) => <option key={c}>{c}</option>)}</select></label>
        <label><span className="mr-label">Lien (facultatif)</span><input data-testid="partner-link" className="mr-input" placeholder="https://" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} /></label>
        <label><span className="mr-label">Logo (PNG, SVG, WEBP ou JPG, fond transparent de préférence)</span><input data-testid="partner-logo-file" type="file" accept=".png,.svg,.webp,.jpg,.jpeg" className="mr-input" onChange={(e) => setFile(e.target.files[0])} /></label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="partner-active" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Actif (affiché sur le site)</label>
        <button data-testid="partner-save" className="mr-btn-primary" disabled={busy || f.name.trim().length < 2 || (!p && !file)} onClick={save}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
      </DialogContent>
    </Dialog>
  );
}

export default function Partners() {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const load = () => api.get("/admin/partners").then(({ data }) => setD(data));
  useEffect(() => {
    load();
  }, []);
  const move = async (i, dir) => {
    const ids = d.items.map((x) => x.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    await api.put("/admin/partners/order", { ids });
    load();
  };
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Contenu" title="Partenaires">
        <button data-testid="partner-add" className="mr-btn-primary" onClick={() => setEdit({})}><Plus size={15} /> Ajouter</button>
      </PageTitle>
      <p className="text-sm text-mr-text2 mb-4">Les logos actifs défilent sur la page d'accueil, en gris puis en couleur au survol. Sans logo actif, le bandeau est masqué.</p>
      {!d ? <Loader /> : d.items.length === 0 ? <EmptyState title="Aucun partenaire" text="Ajoutez compagnies aériennes, agences de voyage, transitaires et banques." testId="partners-empty" /> : (
        <div className="space-y-2">{d.items.map((p, i) => (
          <div key={p.id} className="mr-card p-3 flex items-center gap-4" data-testid="partner-row">
            <div className="w-28 h-12 grid place-items-center bg-white rounded-[3px] border border-mr-border"><img src={p.logo_url} alt={p.name} className="max-h-9 max-w-[96px] object-contain" /></div>
            <div className="flex-1 min-w-0"><p className="font-medium">{p.name}</p><p className="text-xs text-mr-muted">{p.category} · {p.active ? "actif" : "inactif"}{p.link ? ` · ${p.link}` : ""}</p></div>
            <button aria-label="Monter" data-testid="partner-up" disabled={i === 0} className="mr-btn-ghost px-2" onClick={() => move(i, -1)}><ArrowUp size={15} /></button>
            <button aria-label="Descendre" data-testid="partner-down" disabled={i === d.items.length - 1} className="mr-btn-ghost px-2" onClick={() => move(i, 1)}><ArrowDown size={15} /></button>
            <button aria-label="Modifier" data-testid="partner-edit" className="mr-btn-ghost px-2" onClick={() => setEdit(p)}><Pencil size={15} /></button>
            <button aria-label="Supprimer" data-testid="partner-delete" className="mr-btn-ghost px-2 text-mr-danger" onClick={() => setDel(p)}><Trash2 size={15} /></button>
          </div>))}</div>
      )}
      {edit && d && <PartnerForm p={edit.id ? edit : null} cats={d.categories} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {del && <ConfirmDialog title={`Supprimer « ${del.name} » ?`} danger label="Supprimer" onClose={() => setDel(null)}
        onConfirm={async () => { await api.delete(`/admin/partners/${del.id}`); toast.success("Partenaire supprimé"); load(); }} />}
    </div>
  );
}
